import { cx } from 'emotion';
import React, {
  ChangeEventHandler,
  createContext,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import {
  radioButtonLabel,
  labelClassName,
  radioButton,
  input,
  checked,
  radioButtonBullet,
  radioBulletChecked,
} from './style.css';

// Create context for the radio group state
type RadioContextType = {
  selectedValue: string;
  groupName: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

const RadioContext = createContext<RadioContextType | undefined>(undefined);

const useRadioContext = () => {
  const context = useContext(RadioContext);
  if (context === undefined) {
    throw new Error('useRadioContext must be used within a RadioGroup component');
  }
  return context;
};

type RadioGroupProps = {
  groupName: string;
  selectedValue: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  children: ReactNode;
};

type RadioOptionProps = {
  value: string;
  label: string;
};

const RadioSelect = () => {
  throw new Error(
    'RadioSelect should not be used directly. Use RadioSelect.Group with RadioSelect.Option children'
  );
};

const RadioGroup = ({
  groupName,
  selectedValue,
  onChange,
  children,
}: RadioGroupProps) => {
  const contextValue = useMemo(() => ({
    selectedValue,
    groupName,
    onChange,
  }), [ selectedValue, groupName, onChange ]);

  return (
    <RadioContext.Provider value={contextValue}>
      {children}
    </RadioContext.Provider>
  );
};

const RadioOption = ({ value, label }: RadioOptionProps) => {
  const { selectedValue, groupName, onChange } = useRadioContext();

  return (
    <React.Fragment>
      <label className={radioButtonLabel} tabIndex={-1} htmlFor={value}>
        <p className={labelClassName}>{label}</p>
        <div
          className={cx(radioButton, {
            [checked]: selectedValue === value,
          })}
        >
          <div className={cx(radioButtonBullet, {
            [radioBulletChecked]: selectedValue === value,
          })}
          />
        </div>
      </label>
      <input
        className={input}
        type="radio"
        tabIndex={-1}
        name={groupName}
        value={value}
        checked={selectedValue === value}
        onChange={onChange}
        id={value}
      />
    </React.Fragment>
  );
};

type LegacyRadioSelectProps = {
  onChange: ChangeEventHandler<HTMLInputElement>;
  options: Array<{ value: string; label: string }>;
  groupName: string;
  selectedValue: string;
};

const LegacyRadioSelect = ({
  options,
  groupName,
  selectedValue,
  onChange,
}: LegacyRadioSelectProps) => (
  <RadioGroup
    groupName={groupName}
    selectedValue={selectedValue}
    onChange={onChange}
  >
    {options.map((option) => (
      <RadioOption key={option.value} value={option.value} label={option.label} />
    ))}
  </RadioGroup>
);

RadioSelect.Group = RadioGroup;
RadioSelect.Option = RadioOption;

export default RadioSelect;
export { LegacyRadioSelect, useRadioContext };
